import styled from "@emotion/styled";
import { useEffect } from "react";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { DesignToken, ShadowToken } from "@livechat/design-system-react-components";

import { ColLabel, LightTable } from "components/light-table";
import { SearchNoMatches } from "components/search-no-matches";
import { AgentActions, selectAgentsAlphabetically } from "store/entities/agents";
import { ITeam } from "store/entities/teams";
import { ISort } from "store/views/agents";
import { withVar } from "themes/helpers";

import { TeamsRow } from "./TeamsRow";

const TeamsLightTable = styled(LightTable)`
  thead tr th:first-child {
    width: 60px;
    text-align: center;
  }

  tbody tr td:nth-child(3) {
    display: none;
    // Show members only on wide screens
    @media (min-width: 706px) {
      display: table-cell;
    }
  }

  tbody tr td:first-child {
    width: 60px;
    padding-top: 13px;
    padding-bottom: 13px;
    text-align: center;
  }

  tbody tr td {
    word-break: break-all;
  }

  .add-team-label {
    font-size: 16px;
    color: ${withVar(DesignToken.ActionPrimaryDefault)};
  }
`;

const TeamsLightTableHead = styled.table`
  background-color: ${withVar(DesignToken.SurfacePrimaryDefault)};
  width: 100%;

  thead tr th:first-child {
    width: 60px;
    text-align: center;
  }

  thead tr th:nth-child(3) {
    display: none;
    @media (min-width: 706px) {
      display: table-cell;
    }
  }

  thead tr {
    box-shadow: ${withVar(ShadowToken.DividerBottom)};
    border-bottom: solid 1px ${withVar(DesignToken.BorderBasicSecondary)};
    height: 35px;
    font-size: 12px;
    font-weight: 400;
    color: ${withVar(DesignToken.ContentBasicDisabled)};
    position: relative;
    vertical-align: middle;
    text-align: left;
    padding: 0 10px;
    text-transform: uppercase;
  }

  thead tr th {
    height: 35px;
    font-size: 12px;
    font-weight: 400;
    color: ${withVar(DesignToken.ContentBasicDisabled)};
    padding: 0 10px;
    text-transform: uppercase;
    position: relative;
    vertical-align: middle;
    text-align: left;
  }
`;

const TeamsLightTableWrapper = styled.div`
  flex: 0 1 auto;
  min-height: 72px;
  overflow: auto;
`;

interface IProps {
  teams: ITeam[];
  filterQuery: string;
  selectedTeam: ITeam;
  canAddTeam: boolean;
  sort: ISort;
  onSelectTeam(team: ITeam): void;
  onCreateTeam(): void;
  onDoubleClickTeam(team: ITeam): void;
  onSortTeams(field: string, desc: boolean): void;
}

export const TeamsList: React.FC<IProps> = ({
  teams,
  filterQuery,
  selectedTeam,
  sort,
  onSelectTeam,
  onDoubleClickTeam,
  onSortTeams,
}) => {
  const dispatch = useDispatch();
  const agents = useSelector(selectAgentsAlphabetically);

  useEffect(() => {
    dispatch(AgentActions.fetchCollection({}));
  }, []);

  return (
    <>
      <TeamsLightTableHead>
        <thead>
          <tr>
            <th />

            <th>
              <ColLabel active={sort.field === "name"} desc={sort.desc} onClick={() => onSortTeams("name", !sort.desc)}>
                Name
              </ColLabel>
            </th>
            <th>
              <ColLabel
                active={sort.field === "agents"}
                desc={sort.desc}
                onClick={() => onSortTeams("agents", !sort.desc)}
              >
                Agents
              </ColLabel>
            </th>
          </tr>
        </thead>
      </TeamsLightTableHead>
      <TeamsLightTableWrapper>
        <TeamsLightTable>
          <tbody>
            {teams.map(team => (
              <TeamsRow
                key={team.id}
                agents={agents}
                team={team}
                isSelected={team === selectedTeam}
                handleTeamSelect={onSelectTeam}
                handleTeamDoubleClick={onDoubleClickTeam}
              />
            ))}
          </tbody>
        </TeamsLightTable>
        {filterQuery && teams.length === 0 && <SearchNoMatches />}
      </TeamsLightTableWrapper>
    </>
  );
};
